import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  DigitalIdAuthenticationPayload,
  DigitalIdClientInfoPayload,
  DigitalIdComponentEvent,
  DigitalIdOnCompleteCallbackFn,
  DigitalIdResult,
  IndividualWithResult,
  FrankieOneKycResult,
} from '@portal-workspace/grow-shared-library';
import { Observable } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// Legacy types for backward compatibility
export type DigitalIdGetClientIdFn =
  (token?: string) => Observable<DigitalIdClientInfoPayload>;
export type DigitalIdAuthenticateFn = (
  data: { code: string; transaction_id: string },
  type: 'basic' | 'premium'
) => Observable<DigitalIdAuthenticationPayload>;

export interface DigitalIdVerifyDialogData {
  individual: IndividualWithResult,
  kycResult: DigitalIdResult,
  getClientFn: DigitalIdGetClientIdFn,
  authenticateFn: DigitalIdAuthenticateFn,
  digitalIdOnCompleteCallbackFn: any,
  // FrankieOne specific
  verificationUrl?: string,
  frankieOneResult?: FrankieOneKycResult,
}

// FrankieOne verification states
export type FrankieOneVerificationState = 'NOT_STARTED' | 'SMS_SENT' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

// Event emitted when FrankieOne verification state changes
export interface FrankieOneVerificationEvent {
  state: FrankieOneVerificationState;
  verificationUrl?: string;
  entityId?: string;
  requestId?: string;
  error?: string;
}

export let counter = 1;

@Component({
  selector: 'app-digital-id',
  templateUrl: './digital-id.component.html',
  styleUrls: ['./digital-id.component.scss'],
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule],
})
export class DigitalIdComponent implements OnInit {
  @Output() events: EventEmitter<DigitalIdComponentEvent> = new EventEmitter();
  @Output() frankieOneEvents: EventEmitter<FrankieOneVerificationEvent> = new EventEmitter();
  @Output() initiateVerification: EventEmitter<void> = new EventEmitter();
  @Output() checkStatus: EventEmitter<void> = new EventEmitter();

  @Input({required: false}) selectorId = `digitalClient-${counter++}`;
  @Input({required: false}) verificationSessionToken?: string = undefined;
  @Input({required: false}) type: 'basic' | 'premium' = 'basic';

  // Legacy inputs for backward compatibility (deprecated - will be removed)
  @Input({ required: false }) getClientFn?: DigitalIdGetClientIdFn;
  @Input({ required: false }) authenticateFn?: DigitalIdAuthenticateFn;
  @Input({required: false}) onCompleteCallback?: DigitalIdOnCompleteCallbackFn;
  @Input({ required: false }) onClick?: () => void;
  @Input({required: false}) token?: string;
  @Input({ required: false }) uxMode?: string = 'popup';

  // FrankieOne specific inputs
  @Input({ required: false }) verificationUrl?: string;
  @Input({ required: false }) verificationState: FrankieOneVerificationState = 'NOT_STARTED';
  @Input({ required: false }) isLoading: boolean = false;
  @Input({ required: false }) recipientName?: string;
  @Input({ required: false }) recipientMobile?: string;

  ngOnInit(): void {
    // No initialization needed for FrankieOne - it's a simple button-based flow
  }

  /**
   * Called when user clicks "Verify Identity" button
   * Emits event to parent to call backend and initiate FrankieOne verification
   */
  onInitiateVerification(): void {
    if (this.onClick) {
      this.onClick();
    }
    this.initiateVerification.emit();
  }

  /**
   * Called when user clicks "Check Status" button
   * Emits event to parent to refresh verification status from backend
   */
  onCheckStatus(): void {
    this.checkStatus.emit();
  }

  /**
   * Opens the FrankieOne verification URL in a new tab
   * This is a fallback option if user didn't receive SMS
   */
  openVerificationUrl(): void {
    if (this.verificationUrl) {
      window.open(this.verificationUrl, '_blank');
    }
  }

  /**
   * Returns display text based on current verification state
   */
  getStatusMessage(): string {
    switch (this.verificationState) {
      case 'NOT_STARTED':
        return '';
      case 'SMS_SENT':
      case 'IN_PROGRESS':
        return `Verification SMS has been sent${this.recipientMobile ? ` to ${this.maskMobile(this.recipientMobile)}` : ''}. Please complete the verification using the link in the SMS.`;
      case 'COMPLETED':
        return 'Identity verification has been completed successfully.';
      case 'FAILED':
        return 'Identity verification was not successful. Please try again.';
      default:
        return '';
    }
  }

  /**
   * Masks mobile number for display (e.g., 0412345678 -> 04****5678)
   */
  private maskMobile(mobile: string): string {
    if (mobile.length < 6) return mobile;
    return mobile.substring(0, 2) + '****' + mobile.substring(mobile.length - 4);
  }

  /**
   * Returns whether the verify button should be shown
   */
  showVerifyButton(): boolean {
    return this.verificationState === 'NOT_STARTED' || this.verificationState === 'FAILED';
  }

  /**
   * Returns whether the SMS sent message and fallback link should be shown
   */
  showSmsSentMessage(): boolean {
    return this.verificationState === 'SMS_SENT' || this.verificationState === 'IN_PROGRESS';
  }

  /**
   * Returns whether the success message should be shown
   */
  showSuccessMessage(): boolean {
    return this.verificationState === 'COMPLETED';
  }

  /**
   * Returns whether the check status button should be shown
   */
  showCheckStatusButton(): boolean {
    return this.verificationState === 'SMS_SENT' || this.verificationState === 'IN_PROGRESS';
  }
}
